import * as React from 'react'

import {
  IPermitTypeField,
  IPermitTypeFieldDefaultOptionsInput,
  SortDirection,
} from '~/client/graph'
import { toFilterOptions } from '~/client/src/shared/components/ResourcePicker/filterCompoundIds'
import {
  areLocalDefaultsEqual,
  ILocalDefaults,
  parseDefaultOptionsFlat,
} from '~/client/src/shared/components/ResourcePicker/parseDefaultOptions'
import SortOrder from '~/client/src/shared/enums/SortOrder'

function toDefaultOptionsInput(
  defaults: ILocalDefaults,
): IPermitTypeFieldDefaultOptionsInput {
  return {
    sortOption: defaults.sortKey ?? undefined,
    sortDirection: defaults.sortKey
      ? defaults.sortOrder === SortOrder.ASC
        ? SortDirection.Ascending
        : SortDirection.Descending
      : undefined,
    groupOption: defaults.groupByKey ?? undefined,
    filterOptions:
      defaults.filterSelection.length > 0
        ? toFilterOptions(defaults.filterSelection)
        : undefined,
  }
}

export function useResourceFieldDefaults(
  field: IPermitTypeField,
  onChange: (updatedField: IPermitTypeField) => void,
) {
  const fieldDefaults = React.useMemo(
    () => parseDefaultOptionsFlat(field.defaultOptions),
    [field.defaultOptions],
  )
  const [localState, setLocalState] = React.useState(() => ({
    incoming: fieldDefaults,
    defaults: fieldDefaults,
  }))
  if (!areLocalDefaultsEqual(localState.incoming, fieldDefaults)) {
    setLocalState({ incoming: fieldDefaults, defaults: fieldDefaults })
  }
  const defaults = areLocalDefaultsEqual(localState.incoming, fieldDefaults)
    ? localState.defaults
    : fieldDefaults

  const applyDefaults = React.useCallback(
    (next: ILocalDefaults) => {
      setLocalState(prev => ({ ...prev, defaults: next }))
      onChange({ ...field, defaultOptions: toDefaultOptionsInput(next) })
    },
    [field, onChange],
  )

  const setSortDefaults = React.useCallback(
    (key: string | null, order: SortOrder) => {
      applyDefaults({ ...defaults, sortKey: key, sortOrder: order })
    },
    [applyDefaults, defaults],
  )

  const setGroupByDefault = React.useCallback(
    (key: string | null) => {
      applyDefaults({ ...defaults, groupByKey: key })
    },
    [applyDefaults, defaults],
  )

  const setFilterDefaults = React.useCallback(
    (selection: string[]) => {
      applyDefaults({ ...defaults, filterSelection: selection })
    },
    [applyDefaults, defaults],
  )

  const setAllDefaults = React.useCallback(
    (
      sortKey: string | null,
      sortOrder: SortOrder,
      groupByKey: string | null,
      filterSelection: string[],
    ) => {
      applyDefaults({ sortKey, sortOrder, groupByKey, filterSelection })
    },
    [applyDefaults],
  )

  return {
    defaults,
    applyDefaults,
    setSortDefaults,
    setGroupByDefault,
    setFilterDefaults,
    setAllDefaults,
  }
}
